import { useGetAllProductsByMenu } from 'api/hooks/useGetAllProductsByMenu'
import { useGetCategories } from 'api/hooks/useGetCategories'
import { useSetCategoryHidden } from 'api/hooks/useSetCategoryHidden'
import { Category } from 'components/menus/admin/category'
import { CategoryConfirmModal } from 'components/menus/admin/category-confirm-modal'
import { NoResult } from 'components/menus/admin/no-result'
import { Product } from 'components/menus/admin/product'
import { Search } from 'components/menus/admin/search'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useHiddenProducts } from 'api/hooks/useHiddenProducts'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid'

export interface Products {
  product_id?: string | null | undefined
  category_id?: string | null | undefined
  relational_product_id?: string | null | undefined
  description?: string | null | undefined
  title?: string | null | undefined
  is_unavailable?: boolean | null | undefined
}

interface CategorySummary {
  category_id?: string | null | undefined
  category_title?: string | null | undefined
  menu_title?: string | null | undefined
  total_products?: number | null | undefined
  hidden_products_count?: number | null | undefined
  visible_products_count?: number | null | undefined
  is_offline?: boolean | null | undefined
  is_partially_offline?: boolean | null | undefined
}

type AdminTab = 'categories' | 'products'

const getPublicCategoryId = (category: any) => category?.category?.id?.toString()

const getPublicCategoryTitle = (category: any) =>
  (category?.category?.attributes?.menu_title || category?.category?.attributes?.title)?.toString()

const getAdminCategoryTitle = (category: CategorySummary) =>
  (category.menu_title || category.category_title)?.toString()

const sortCategoriesByPublicOrder = (categories: CategorySummary[], publicCategories: any[]) => {
  if (!publicCategories.length) {
    return categories
  }

  const publicCategoryOrderById = new Map<string, number>()
  const publicCategoryOrderByTitle = new Map<string, number>()

  publicCategories.forEach((category, index) => {
    const id = getPublicCategoryId(category)
    const title = getPublicCategoryTitle(category)

    if (id) {
      publicCategoryOrderById.set(id, index)
    }

    if (title) {
      publicCategoryOrderByTitle.set(title, index)
    }
  })

  return [...categories].sort((leftCategory, rightCategory) => {
    const leftOrder =
      publicCategoryOrderById.get(leftCategory.category_id?.toString() ?? '') ??
      publicCategoryOrderByTitle.get(getAdminCategoryTitle(leftCategory) ?? '') ??
      Number.MAX_SAFE_INTEGER
    const rightOrder =
      publicCategoryOrderById.get(rightCategory.category_id?.toString() ?? '') ??
      publicCategoryOrderByTitle.get(getAdminCategoryTitle(rightCategory) ?? '') ??
      Number.MAX_SAFE_INTEGER

    return leftOrder - rightOrder
  })
}

export const RestaurantAdmin = () => {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<AdminTab>('products')
  const [freeze, setFreeze] = useState(false)
  const [showOfflineCategoriesOnly, setShowOfflineCategoriesOnly] = useState(false)
  const [showOfflineFirst, setShowOfflineFirst] = useState(false)
  const [pendingCategory, setPendingCategory] = useState<CategorySummary | null>(null)
  const [categoryActionError, setCategoryActionError] = useState<string | null>(null)

  const [products, setProducts] = useState<any>()
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const password = searchParams.get('password')
  const { data, productsIsFetched, errors, isError, dataUpdatedAt, refetch } = useGetAllProductsByMenu(password)
  const { data: publicCategories } = useGetCategories(navigator.language)

  const { hidden_products } = useHiddenProducts()

  const { refetch: refetchHiddenProducts } = useHiddenProducts()
  const { mutate: setCategoryHidden, isLoading: isCategoryActionLoading } = useSetCategoryHidden()
  const isCategoryActionBusy = isCategoryActionLoading || freeze

  useEffect(() => {
    let filteredProducts = data?.products?.filter((p) => p?.title?.toLowerCase().includes(search.toLowerCase())) ?? []

    if (showOfflineFirst && filteredProducts) {
      filteredProducts = filteredProducts.filter(
        (p) => p && p.product_id && hidden_products?.includes(parseInt(p.product_id))
      )
    }

    setProducts(filteredProducts)
  }, [search, data, dataUpdatedAt, showOfflineFirst, hidden_products])

  const categories = useMemo(
    () => sortCategoriesByPublicOrder((data?.categories ?? []).filter(Boolean) as CategorySummary[], publicCategories),
    [data?.categories, publicCategories]
  )
  const filteredCategories = showOfflineCategoriesOnly
    ? categories.filter((category) => Boolean(category.is_offline))
    : categories

  if (!productsIsFetched) {
    return <div className="h-screen">Loading...</div>
  }

  if (isError) {
    return (
      <div className="h-screen">
        {errors.map((m: string) => (
          <p>{m}</p>
        ))}
      </div>
    )
  }

  const getMutationErrorMessage = (error: any) => {
    return error?.response?.errors?.[0]?.message || error?.message || 'Failed to update category visibility.'
  }

  const handleConfirmCategoryAction = async () => {
    if (!pendingCategory?.category_id || !slug) {
      return
    }

    const nextHiddenState = !pendingCategory.is_offline

    setCategoryActionError(null)
    setFreeze(true)

    try {
      await setCategoryHidden({
        category_id: pendingCategory.category_id,
        slug_menu: slug,
        hidden: nextHiddenState
      })
      await Promise.all([refetch(), refetchHiddenProducts()])
      setPendingCategory(null)
    } catch (error) {
      setCategoryActionError(getMutationErrorMessage(error))
    } finally {
      setFreeze(false)
    }
  }

  return (
    <>
      <div className="bg-gray-100 overflow-x-hidden h-screen ">
        <div className="flex justify-center  h-full w-full  container max-w-[768px]  mx-auto">
          <div className="w-full p-5">
            <div className="flex items-center w-full justify-between -mt-2">
              <h1 className="text-[20px] text-right  text-gray-800">{data?.title}</h1>
              <p>App v-0.3</p>
            </div>

            <div className="mb-4 mt-4 rounded-lg bg-white p-1 shadow-sm">
              <div className="grid grid-cols-2 gap-1">
                <button
                  type="button"
                  onClick={() => setActiveTab('products')}
                  className={`rounded-md px-4 py-3 text-sm font-semibold transition ${
                    activeTab === 'products' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Products ({products?.length ?? 0})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('categories')}
                  className={`rounded-md px-4 py-3 text-sm font-semibold transition ${
                    activeTab === 'categories' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Categories ({filteredCategories.length})
                </button>
              </div>
            </div>
            {categoryActionError && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {categoryActionError}
              </div>
            )}
            {activeTab === 'categories' && (
              <>
                {categories?.length > 0 ? (
                  <div className="mb-6">
                    <div>
                      <button
                        className={`justify-center inline-flex items-center px-4 py-2 border border-transparent shadow-sm font-medium rounded-md text-white 
                    ${showOfflineCategoriesOnly ? 'bg-red-500 hover:bg-red-600 ' : 'bg-gray-400 hover:bg-gray-500 '}`}
                        onClick={() => {
                          setShowOfflineCategoriesOnly((prev) => !prev)
                        }}
                      >
                        {showOfflineCategoriesOnly ? (
                          <CheckCircleIcon className="h-6 w-6 inline mr-2" />
                        ) : (
                          <XCircleIcon className="h-6 w-6 inline mr-2" />
                        )}
                        Only offline categories
                      </button>
                      <div className="flex items-center justify-between mb-2 mt-3">
                        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Categories</h2>
                        <p className="text-right text-gray-600 text-[12px]">
                          {filteredCategories.length} categor{filteredCategories.length > 1 ? 'ies' : 'y'}
                        </p>
                      </div>
                    </div>

                    {filteredCategories.map((category: CategorySummary) => {
                      const categoryTitle = category.menu_title || category.category_title || 'Untitled category'

                      return (
                        <Category
                          key={category.category_id}
                          title={categoryTitle}
                          totalProducts={category.total_products ?? 0}
                          hiddenProductsCount={category.hidden_products_count ?? 0}
                          isOffline={Boolean(category.is_offline)}
                          isPartiallyOffline={Boolean(category.is_partially_offline)}
                          freeze={freeze}
                          onClick={() => {
                            setCategoryActionError(null)
                            setPendingCategory(category)
                          }}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <NoResult />
                )}
              </>
            )}

            {activeTab === 'products' && (
              <>
                <div className="mb-4">
                  <Search setQuery={(query) => setSearch(query)} />
                </div>
                {products?.length === 0 && <NoResult />}
                {products?.length > 0 && (
                  <div>
                    <button
                      className={`justify-center inline-flex items-center px-4 py-2 border border-transparent shadow-sm font-medium rounded-md text-white 
                    ${showOfflineFirst ? 'bg-red-500 hover:bg-red-600 ' : 'bg-gray-400 hover:bg-gray-500 '}`}
                      onClick={() => {
                        setShowOfflineFirst((prev) => !prev)
                        refetch()
                        refetchHiddenProducts()
                      }}
                    >
                      {showOfflineFirst ? (
                        <CheckCircleIcon className="h-6 w-6 inline mr-2" />
                      ) : (
                        <XCircleIcon className="h-6 w-6 inline mr-2" />
                      )}
                      Only offline products
                    </button>
                    <p className="mb-2 text-right text-gray-600 text-[12px]">
                      {products?.length} product{products?.length > 1 && 's'}
                    </p>
                  </div>
                )}

                {products?.map((p: any, idx: number) => {
                  const id = `${p?.product_id}-${idx}`

                  return (
                    <Product
                      setFreeze={(bool) => {
                        setFreeze(bool)
                      }}
                      freeze={freeze}
                      title={p.title}
                      description={p.description}
                      productId={p.product_id}
                      pairingProductId={p?.pairing_product_id}
                      pairingProduct={p.pairing_product}
                      categoryId={p.category_id}
                      relationalProductId={p.relational_product_id}
                      key={id}
                      isHidden={hidden_products?.includes(parseInt(p.product_id))}
                      isPairingHidden={hidden_products?.includes(parseInt(p.pairing_product_id))}
                      isUnavailable={p.is_unavailable}
                      refetchHiddenProducts={refetchHiddenProducts}
                    />
                  )
                })}
              </>
            )}
          </div>
        </div>
      </div>
      {pendingCategory && (
        <CategoryConfirmModal
          isOpen={Boolean(pendingCategory)}
          isLoading={isCategoryActionBusy}
          categoryTitle={pendingCategory.menu_title || pendingCategory.category_title || 'Untitled category'}
          totalProducts={pendingCategory.total_products ?? 0}
          hiddenProductsCount={pendingCategory.hidden_products_count ?? 0}
          nextHiddenState={!pendingCategory.is_offline}
          onClose={() => {
            if (!isCategoryActionBusy) {
              setPendingCategory(null)
            }
          }}
          onConfirm={handleConfirmCategoryAction}
        />
      )}
    </>
  )
}
